import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
} from "@material-ui/core";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../lib";

/**
 * Status drives the colour of the whole row, as the legacy app did — the
 * status control itself stays plain. The tints are readable at a glance but
 * stay well short of the legacy page's solid pastels, which at a hundred rows
 * stop being information and start being noise.
 */
const STATUS_STYLE = {
  0: { label: "Cancelled", rgb: "183,28,28" },
  1: { label: "Pending", rgb: "13,71,161" },
  2: { label: "Verifying", rgb: "224,168,0" },
  3: { label: "Processing", rgb: "27,94,32" },
  4: { label: "Shipped", rgb: "106,27,154" },
  // Prod tints Returned in tan (#d0beae) rather than grey — grey at this
  // opacity reads as "no status" instead of a status.
  5: { label: "Returned", rgb: "141,110,79" },
};

/** One tint class per status, plus a stronger hover. */
const statusRowStyles = Object.entries(STATUS_STYLE).reduce((acc, [id, s]) => {
  acc[`status${id}`] = {
    backgroundColor: `rgba(${s.rgb}, 0.18)`,
    "&:hover": { backgroundColor: `rgba(${s.rgb}, 0.28)` },
  };
  return acc;
}, {});

const VIEWS = [
  { key: "recent", label: "Recent" },
  { key: "active", label: "Active" },
  { key: "preowned", label: "Pre-Owned" },
];

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2.5) },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 18, fontWeight: 500, flex: "none" },
  spacer: { flex: 1 },
  search: {
    width: 320,
    "& .MuiOutlinedInput-root": { height: 31 },
    "& .MuiOutlinedInput-input": { padding: "0 4px", fontSize: 13 },
    "& .MuiInputAdornment-root": { marginRight: 2 },
  },
  searchIcon: { fontSize: 17, color: theme.palette.text.secondary },
  count: { color: theme.palette.text.secondary, fontSize: 13 },
  clearBtn: { marginLeft: 6, fontSize: 12 },
  table: { tableLayout: "fixed" },
  row: {
    cursor: "pointer",
    height: 36,
    transition: "background-color .15s ease",
    "&:hover": { backgroundColor: "rgba(49,81,121,0.05)" },
  },
  ...statusRowStyles,
  // Tight vertical padding: the inline Select and TextField bring their own
  // height, and the default cell padding on top pushed rows past 50px.
  cell: { fontSize: 13, padding: "0 10px", height: 36 },
  headCell: { fontSize: 13, padding: "7px 10px", whiteSpace: "nowrap" },
  /** Truncation without the muted colour — for columns that carry real text. */
  truncate: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  num: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
  /* Plain text with a caret — the row's tint already carries the status. */
  statusSelect: {
    fontSize: 12,
    color: "rgba(0,0,0,0.72)",
    "&:before, &:after": { display: "none" },
    "& .MuiSelect-select": {
      padding: "2px 18px 2px 0",
      background: "transparent",
    },
    "& .MuiSelect-icon": {
      right: 0,
      fontSize: 17,
      color: "rgba(0,0,0,0.32)",
    },
    "&:hover .MuiSelect-icon": { color: "rgba(0,0,0,0.6)" },
  },
  statusMenu: {
    marginTop: 2,
    "& .MuiMenuItem-root": {
      fontSize: 12.5,
      minHeight: 0,
      lineHeight: 1.5,
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  /* The pre-owned marker occupies a fixed slot on EVERY row, empty or not.
     Rendering it only where it applies pushed those order numbers 21px right
     of the rest, so the column never lined up. */
  pre: {
    display: "inline-block",
    width: 15,
    marginRight: 6,
    borderRadius: 3,
    textAlign: "center",
    fontSize: 10.5,
    fontWeight: 700,
    lineHeight: "15px",
  },
  preOn: {
    color: "#4a148c",
    background: "rgba(74,20,140,0.10)",
  },
  itemLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  /* Inline editors. Underline-only so a table of 100 rows does not read as a
     wall of boxes; the control announces itself on hover. */
  noteInput: {
    fontSize: 12.5,
    width: "100%",
    "& input": { paddingTop: 3, paddingBottom: 3 },
    "&:before": { borderBottomColor: "transparent" },
    "&:hover:before": { borderBottomColor: "rgba(0,0,0,0.25) !important" },
  },
  expandBtn: {
    minWidth: 0,
    width: 22,
    height: 22,
    padding: 0,
    lineHeight: "20px",
    fontSize: 15,
    fontWeight: 600,
    color: "rgba(0,0,0,0.45)",
    border: "1px solid rgba(0,0,0,0.2)",
    borderRadius: 3,
    background: "rgba(255,255,255,0.7)",
    "&:hover": { color: "rgba(0,0,0,0.8)", borderColor: "rgba(0,0,0,0.4)" },
  },
  // Keeps the parent row's tint: an expansion is part of the record above it,
  // not a separate thing.
  detailRow: { "& > td": { borderBottom: "1px solid rgba(0,0,0,0.12)" } },
  detailCell: { padding: "8px 12px 10px 44px", fontSize: 12.5, lineHeight: 1.75 },
  detailLabel: { fontWeight: 700 },
  special: { color: "#b71c1c", fontWeight: 600 },
  repeatBox: { padding: "0 6px" },
  saving: { opacity: 0.5 },
  saved: {
    color: "#1b5e20",
    fontSize: 11,
    marginLeft: 6,
    whiteSpace: "nowrap",
  },
  notes: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  empty: { padding: theme.spacing(5, 2), textAlign: "center", color: theme.palette.text.secondary },
  error: { color: "#c62828", marginTop: theme.spacing(1.5) },
}));

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

/** Server sends MySQL datetimes; treat them as-is rather than as UTC. */
const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? String(s)
    : d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      });
};

const buyer = (r) =>
  [r.bill_first_name, r.bill_mi, r.bill_last_name]
    .filter((p) => p && p !== "x")
    .join(" ")
    .trim();

/** Orders list. Port of the legacy ptorder8300 index. */
export function OrdersPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  /**
   * Advanced criteria come from the search screen as query parameters. They
   * are forwarded verbatim; the view/search/page/limit controls below own
   * their own keys and are stripped out first.
   */
  const criteria = React.useMemo(() => {
    const own = new Set(["view", "search", "page", "limit"]);
    const out = {};
    new URLSearchParams(location.search).forEach((v, k) => {
      if (!own.has(k) && String(v).trim() !== "") out[k] = v;
    });
    return out;
  }, [location.search]);

  const criteriaCount = Object.keys(criteria).length;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [view, setView] = useState("recent");
  const [search, setSearch] = useState("");
  // Applied separately so typing does not fire a query per keystroke.
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // order_id -> "saving" | "saved", for the inline edits.
  const [saveState, setSaveState] = useState({});
  // Expansion is lazy: the summary costs a request, so it is fetched on first
  // open and kept, rather than loaded for a hundred rows nobody expanded.
  const [open, setOpen] = useState({});
  const [detail, setDetail] = useState({});

  /**
   * The expanded block owns this value, so the write updates that cached copy
   * rather than the table row — the row does not display it.
   */
  const toggleRepeat = (row, d) => async (e) => {
    const previous_customer = e.target.checked ? 1 : 0;
    const id = row.order_id;
    const before = d.order.previous_customer;

    setDetail((s) => ({
      ...s,
      [id]: { ...s[id], order: { ...s[id].order, previous_customer } },
    }));

    const { error: err } = await authPut(`/orders/${id}`, {
      previous_customer: !!previous_customer,
    });
    if (err) {
      setDetail((s) => ({
        ...s,
        [id]: { ...s[id], order: { ...s[id].order, previous_customer: before } },
      }));
      setError(err.message || "Could not save that change.");
    }
  };

  const toggle = (row) => async (e) => {
    e.stopPropagation();
    const id = row.order_id;
    const nowOpen = !open[id];
    setOpen((o) => ({ ...o, [id]: nowOpen }));
    if (!nowOpen || detail[id]) return;

    const { data, error: err } = await authGet(`/orders/${id}`);
    setDetail((d) => ({
      ...d,
      [id]: err ? { failed: err.message || "Could not load details." } : data,
    }));
  };

  const mark = (id, state) =>
    setSaveState((s) => ({ ...s, [id]: state }));

  /**
   * Writes one field and keeps the row in step. Optimistic: the row already
   * shows the new value, so a failure has to put the old one back.
   */
  const patch = useCallback(async (row, body, previous) => {
    mark(row.order_id, "saving");
    const { error: err } = await authPut(`/orders/${row.order_id}`, body);

    if (err) {
      setRows((rs) =>
        rs.map((r) => (r.order_id === row.order_id ? { ...r, ...previous } : r)),
      );
      setError(err.message || "Could not save that change.");
      mark(row.order_id, undefined);
      return;
    }

    setError("");
    mark(row.order_id, "saved");
    setTimeout(() => mark(row.order_id, undefined), 1800);
  }, []);

  const setStatus = (row) => (e) => {
    const order_status_id = Number(e.target.value);
    const previous = { order_status_id: row.order_status_id };
    setRows((rs) =>
      rs.map((r) => (r.order_id === row.order_id ? { ...r, order_status_id } : r)),
    );
    patch(row, { order_status_id }, previous);
  };

  const editNote = (row) => (e) => {
    const notes_customer = e.target.value;
    setRows((rs) =>
      rs.map((r) => (r.order_id === row.order_id ? { ...r, notes_customer } : r)),
    );
  };

  // The row is updated on every keystroke, so the value to revert to has to be
  // captured before typing starts, not read off the row at save time.
  const noteBefore = useRef({});
  const focusNote = (row) => (e) => {
    noteBefore.current[row.order_id] = e.target.value;
  };

  // Saved on blur rather than per keystroke, and only when actually changed.
  const commitNote = (row) => (e) => {
    const notes_customer = e.target.value;
    const original = noteBefore.current[row.order_id] ?? "";
    if (original === notes_customer) return;
    patch(row, { notes_customer }, { notes_customer: original });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      view,
      page: String(page + 1),
      limit: String(limit),
      ...criteria,
    });
    if (applied) params.set("search", applied);

    const { data, error: err } = await authGet(`/orders?${params}`);
    if (err) setError(err.message || "Could not load orders.");
    else {
      setRows(data?.rows ?? []);
      setTotal(Number(data?.total ?? 0));
    }
    setLoading(false);
  }, [view, applied, page, limit, criteria]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = (e) => {
    e.preventDefault();
    setPage(0);
    setApplied(search.trim());
  };

  const choose = (key) => () => {
    setView(key);
    setPage(0);
  };

  /** Editors live inside a clickable row; their clicks must not navigate. */
  const stop = (e) => e.stopPropagation();

  return (
    <Box className={classes.page}>
      <Paper className={classes.panel}>
        <div className={classes.head}>
          <Typography className={classes.title}>Orders</Typography>

          <ButtonGroup size="small">
            {VIEWS.map((v) => (
              <Button
                key={v.key}
                onClick={choose(v.key)}
                variant={view === v.key ? "contained" : "outlined"}
                color={view === v.key ? "primary" : "default"}
              >
                {v.label}
              </Button>
            ))}
          </ButtonGroup>

          <form onSubmit={submit}>
            <TextField
              className={classes.search}
              placeholder="Order #, name, email, phone or model"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={submit}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </form>

          <div className={classes.spacer} />

          {criteriaCount > 0 && (
            <span className={classes.count}>
              {loading ? "Loading…" : `${total.toLocaleString()} found`}
              <Button
                size="small"
                className={classes.clearBtn}
                onClick={() => history.push("/orders")}
              >
                Clear search
              </Button>
            </span>
          )}

          {/* Starts empty; the New on an order copies that customer instead. */}
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push("/orders/new")}
          >
            New
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => history.push("/orders/search")}
          >
            Search
          </Button>
        </div>

        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}

        {loading && !rows.length ? (
          <div className={classes.empty}>
            <CircularProgress size={26} />
          </div>
        ) : !rows.length ? (
          <div className={classes.empty}>
            <Typography variant="body2">
              {applied ? `Nothing matches “${applied}”.` : "No orders in this view."}
            </Typography>
          </div>
        ) : (
          <>
            <Table size="small" className={classes.table}>
              <TableHead>
                {/* Notes is the only column without a width, so it absorbs
                    whatever the fixed ones leave. */}
                <TableRow>
                  <TableCell className={classes.headCell} style={{ width: 34 }} />
                  <TableCell className={classes.headCell} style={{ width: 92 }}>Order #</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 150 }}>Date</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 155 }}>Customer</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 195 }}>Email</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 112 }}>Pay Method</TableCell>
                  <TableCell className={classes.headCell} style={{ width: 128 }}>Status</TableCell>
                  {/* Header reads left with the others; the figures stay right. */}
                  <TableCell className={classes.headCell} style={{ width: 100 }}>
                    Total
                  </TableCell>
                  <TableCell className={classes.headCell}>Notes</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((r) => {
                  const tint = classes[`status${r.order_status_id}`] ?? "";
                  const d = detail[r.order_id];
                  return (
                  <React.Fragment key={r.order_id}>
                    <TableRow
                      className={`${classes.row} ${tint}`}
                      onClick={() => history.push(`/orders/${r.order_id}`)}
                    >
                      <TableCell className={classes.cell} onClick={stop}>
                        <Button
                          className={classes.expandBtn}
                          onClick={toggle(r)}
                          aria-label={open[r.order_id] ? "Collapse" : "Expand"}
                        >
                          {open[r.order_id] ? "−" : "+"}
                        </Button>
                      </TableCell>
                      <TableCell className={classes.cell}>
                        <span
                          className={`${classes.pre} ${
                            Number(r.is_preowned) ? classes.preOn : ""
                          }`}
                          title={Number(r.is_preowned) ? "Pre-owned" : undefined}
                        >
                          {Number(r.is_preowned) ? "P" : ""}
                        </span>
                        {r.order_id}
                      </TableCell>
                      <TableCell className={classes.cell}>{when(r.order_time)}</TableCell>
                      <TableCell className={`${classes.cell} ${classes.truncate}`} title={buyer(r)}>
                        {buyer(r)}
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.notes}`} title={r.email || ""}>
                        {r.email}
                      </TableCell>
                      <TableCell className={classes.cell}>{r.pay_method_sh}</TableCell>
                      {/* Stops a click on an editor from opening the order. */}
                      <TableCell className={classes.cell} onClick={stop}>
                        <Select
                          value={
                            r.order_status_id === null || r.order_status_id === undefined
                              ? ""
                              : Number(r.order_status_id)
                            }
                          onChange={setStatus(r)}
                          disabled={saveState[r.order_id] === "saving"}
                          className={classes.statusSelect}
                          disableUnderline
                          MenuProps={{
                            // v4 anchors the menu over the selected item by
                            // default; null content anchor plus these origins
                            // drop it directly below the field instead.
                            getContentAnchorEl: null,
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            classes: { paper: classes.statusMenu },
                            MenuListProps: { dense: true, disablePadding: true },
                          }}
                        >
                          {r.order_status_id === null && (
                            <MenuItem value="">
                              <em>Not set</em>
                            </MenuItem>
                          )}
                          {Object.entries(STATUS_STYLE).map(([id, v]) => (
                            <MenuItem key={id} value={Number(id)}>
                              {v.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell className={`${classes.cell} ${classes.num}`}>
                        {money(r.items_total)}
                      </TableCell>
                      <TableCell className={classes.cell} onClick={stop}>
                        <Box display="flex" alignItems="center">
                          <TextField
                            variant="standard"
                            fullWidth
                            InputProps={{ className: classes.noteInput }}
                            placeholder="Notes…"
                            value={r.notes_customer ?? ""}
                            onFocus={focusNote(r)}
                            onChange={editNote(r)}
                            onBlur={commitNote(r)}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            disabled={saveState[r.order_id] === "saving"}
                          />
                          {saveState[r.order_id] === "saved" && (
                            <span className={classes.saved}>Saved</span>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {open[r.order_id] && (
                      <TableRow className={`${classes.detailRow} ${tint}`}>
                        <TableCell className={classes.detailCell} colSpan={9}>
                          {!d ? (
                            "Loading…"
                          ) : d.failed ? (
                            <span className={classes.special}>{d.failed}</span>
                          ) : (
                            <>
                              <div>
                                <span className={classes.detailLabel}>Order:</span>{" "}
                                {d.items.map((it, n) => (
                                  <React.Fragment key={it.order_item_id}>
                                    {n > 0 && ", "}({it.qty}){" "}
                                    {it.url ? (
                                      <a
                                        className={classes.itemLink}
                                        href={it.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open this item on the site"
                                      >
                                        {it.title} {it.model_no}
                                      </a>
                                    ) : (
                                      `${it.title} ${it.model_no}`
                                    )}{" "}
                                    {money(it.price)} each
                                    {!!Number(it.price_on_special) && (
                                      <span className={classes.special}> Item On Special!</span>
                                    )}
                                  </React.Fragment>
                                ))}
                                {!d.items.length && <span>No line items</span>}
                                {d.ship?.ship_rate_description && (
                                  <>
                                    {" | "}
                                    <span className={classes.detailLabel}>Shipping:</span>{" "}
                                    {d.ship.ship_rate_description}
                                  </>
                                )}
                              </div>
                              <div>
                                <span className={classes.detailLabel}>Repeat Customer:</span>
                                <Checkbox
                                  size="small"
                                  className={classes.repeatBox}
                                  checked={!!Number(d.order?.previous_customer)}
                                  onChange={toggleRepeat(r, d)}
                                />
                              </div>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onChangePage={(e, p) => setPage(p)}
              rowsPerPage={limit}
              rowsPerPageOptions={[50, 100, 250]}
              onChangeRowsPerPage={(e) => {
                setLimit(Number(e.target.value));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default OrdersPage;
